import React, { useEffect, useState } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  Typography,
} from '@perk-ui/core'
import { Search as SearchIcon } from 'react-feather'
import { useHistory } from 'react-router-dom'

import usePatientCDSearch from '../../../features/query-hooks/usePatientCDSearch'
import PatientSelfOnboardUrls from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(4),
    width: '100%',
    height: '100%',
  },
  cardBox: {
    padding: theme.spacing(4),
    width: '787px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
  },
  addButton: {
    textTransform: 'none',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignSelf: 'flex-end',
  },
  heading: {
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  searchInput: {
    marginBottom: theme.spacing(3),
  },
  icon: {
    marginLeft: theme.spacing(1.5),
    width: theme.spacing(4),
    marginRight: theme.spacing(-2.5),
    color: theme.palette.grey[600],
  },
  resultsWrapper: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    boxShadow: `0 1px 3px rgba(0, 0, 0, 0.05)`,
  },
  resultsContainer: {
    overflowY: 'auto',
  },
  resultItem: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
      cursor: 'pointer',
    },
  },
  noResults: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
  smallAddButton: {
    textTransform: 'none',
    marginTop: theme.spacing(2),
    alignSelf: 'flex-start',
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.875rem',
  },
  infoSegment: {
    display: 'inline-block',
    // only segments that follow another segment get this border
    '& + &': {
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginLeft: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
  },
  infoLabel: {
    fontWeight: theme.typography.fontWeightBold,
    display: 'inline',
  },
  infoValue: {
    fontWeight: theme.typography.fontWeightRegular,
    display: 'inline',
    marginLeft: theme.spacing(0.5),
  },
}))

interface PatientItem {
  id: string
  firstName: string
  lastName: string
  dateOfBirth?: string
  cellPhoneLocal?: string
  email?: string
}

const PatientSearch: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm)
  const [isTyping, setIsTyping] = useState<boolean>(false)

  useEffect(() => {
    setIsTyping(true)
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim())
      setIsTyping(false)
    }, 300)
    return () => clearTimeout(handler)
  }, [searchTerm])

  const {
    data: patientSearchResponse,
    isLoading,
    error,
  } = usePatientCDSearch(
    { search: debouncedTerm, page: 1, perPage: 20 },
    { enabled: debouncedTerm.length >= 2 },
  )

  const resultsArray: PatientItem[] = patientSearchResponse?.patients ?? []

  const handleAddNew = () => {
    history.push(PatientSelfOnboardUrls.newPatient.create())
  }

  const handleSelectPatient = (p: PatientItem) => {
    history.push(PatientSelfOnboardUrls.existingPatientDetails.create(p.id))
  }

  return (
    <Box className={classes.root}>
      {/* Main card (no border here) */}
      <Box className={classes.cardBox}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            size="medium"
            className={classes.addButton}
            onClick={handleAddNew}
          >
            + Add New Patient
          </Button>
        </Box>

        <Typography variant="h2" className={classes.heading}>
          Search for a patient
        </Typography>

        <Typography variant="body1" className={classes.subtitle}>
          Look up a patient to view or update their information.
        </Typography>

        {/* Search input with React Feather icon on left and spinner on right */}
        <TextField
          className={classes.searchInput}
          placeholder="Search by first and last name"
          fullWidth
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.icon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading || isTyping ? <CircularProgress size={20} /> : null}
              </InputAdornment>
            ),
          }}
        />

        {/* Only show results wrapper if user has typed something */}
        {searchTerm !== '' && (
          <Box className={classes.resultsWrapper}>
            {error && (
              <Typography variant="body2" color="error">
                An error occurred.
              </Typography>
            )}

            {!isLoading &&
              resultsArray.length === 0 &&
              debouncedTerm !== '' && (
                <Typography variant="body2" className={classes.noResults}>
                  No patients found.
                </Typography>
              )}

            {/* Scrollable list of patient results */}
            <Box className={classes.resultsContainer}>
              {resultsArray.map((p: PatientItem) => {
                // Format date as MM/DD/YYYY
                const formattedDob = p.dateOfBirth
                  ? new Date(p.dateOfBirth).toLocaleDateString()
                  : ''
                // Format 10-digit phone to (XXX) XXX-XXXX
                const rawPhone = p.cellPhoneLocal || ''
                const formattedPhone =
                  rawPhone.length === 10
                    ? `(${rawPhone.slice(0, 3)}) ${rawPhone.slice(
                        3,
                        6,
                      )}-${rawPhone.slice(6)}`
                    : rawPhone

                return (
                  <Box
                    key={p.id}
                    className={classes.resultItem}
                    onClick={() => handleSelectPatient(p)}
                  >
                    <Typography variant="subtitle1">
                      {p.firstName} {p.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formattedDob && (
                        <span className={classes.infoSegment}>
                          <span className={classes.infoLabel}>DOB:</span>
                          <span className={classes.infoValue}>
                            {formattedDob}
                          </span>
                        </span>
                      )}

                      {formattedPhone && (
                        <span className={classes.infoSegment}>
                          <span className={classes.infoLabel}>Phone:</span>
                          <span className={classes.infoValue}>
                            {formattedPhone}
                          </span>
                        </span>
                      )}

                      {p.email && (
                        <span className={classes.infoSegment}>
                          <span className={classes.infoLabel}>Email:</span>
                          <span className={classes.infoValue}>{p.email}</span>
                        </span>
                      )}
                    </Typography>
                  </Box>
                )
              })}
            </Box>

            {/* Small Add New Patient button below results */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.smallAddButton}
              onClick={handleAddNew}
            >
              + Add New Patient
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default PatientSearch
